import { EventQuery } from '@/stores/useEventStore';
import {
  CreateEventForm,
  Event,
  EventUser,
  MeetingRequest,
} from '@/types/event';
import { Query } from '@/types/query';
import { PaginatedResponse, Response } from '@/types/response';
import { api } from '@/utils/axios';

export const getEvents = async (
  query: Query,
): Promise<PaginatedResponse<Event> | Response<Event[]>> => {
  const response = await api.get('app-web/events', { params: query });
  return response.data;
};

export const createEvent = async (
  event: CreateEventForm,
): Promise<Response<Event>> => {
  const response = await api.post('app-web/events', event);
  return response.data;
};

export const showEvent = async (eventId: number): Promise<Response<Event>> => {
  const response = await api.get(`app-web/events/${eventId}`);
  return response.data;
};

export const updateEvent = async (
  eventId: number,
  event: Partial<Event>,
): Promise<Response<Event>> => {
  const response = await api.put(`app-web/events/${eventId}`, event);
  return response.data;
};

export const deleteEvent = async (eventId: number): Promise<Response<void>> => {
  const response = await api.delete(`app-web/events/${eventId}`);
  return response.data;
};

export const uploadEventCover = async (
  eventId: number,
  file: string,
): Promise<Response<Event>> => {
  const response = await api.post(`app-web/events/${eventId}/cover`, { file });
  return response.data;
};

export const removeEventCover = async (
  eventId: number,
): Promise<Response<Event>> => {
  const response = await api.delete(`app-web/events/${eventId}/cover`);
  return response.data;
};

export const fetchEventUsers = async (
  eventId: number,
  query: EventQuery,
): Promise<PaginatedResponse<EventUser> | Response<EventUser[]>> => {
  const response = await api.get(`app-web/events/${eventId}/users`, {
    params: query,
  });
  return response.data;
};

export const addEventUser = async (
  eventId: number,
  userId: number,
): Promise<Response<EventUser>> => {
  const response = await api.post(`app-web/events/${eventId}/users`, {
    user_id: userId,
  });
  return response.data;
};

export const removeEventUser = async (
  eventId: number,
  userId: number,
): Promise<Response<EventUser>> => {
  const response = await api.delete(
    `app-web/events/${eventId}/users/${userId}`,
  );
  return response.data;
};

export const setUserCard = async (
  eventId: number,
  userId: number,
  cardSlug: string | null,
): Promise<Response<Event>> => {
  const response = await api.post(`app-web/events/${eventId}/card`, {
    user_id: userId,
    card_slug: cardSlug,
  });
  return response.data;
};

export const joinEvent = async (
  eventCode: string,
): Promise<Response<Event>> => {
  const response = await api.post(`app-web/events/join`, {
    event_code: eventCode,
  });
  return response.data;
};

export const sendMeetingRequest = async (
  eventId: number,
  userId: number,
  date: string,
  message: string,
): Promise<Response<MeetingRequest>> => {
  const response = await api.post(`app-web/events/meeting-requests`, {
    event_id: eventId,
    user_id: userId,
    date,
    message,
  });
  return response.data;
};

export const listMeetingRequests = async (
  eventId: number,
): Promise<Response<MeetingRequest[]>> => {
  const response = await api.get(`app-web/events/${eventId}/meeting-requests`);
  return response.data;
};

export const acceptMeetingRequest = async (
  meetingRequestId: number,
): Promise<Response<MeetingRequest>> => {
  const response = await api.post(
    `app-web/events/meeting-requests/${meetingRequestId}/accept`,
  );
  return response.data;
};

export const rejectMeetingRequest = async (
  meetingRequestId: number,
): Promise<Response<MeetingRequest>> => {
  const response = await api.post(
    `app-web/events/meeting-requests/${meetingRequestId}/reject`,
  );
  return response.data;
};

export const replanMeetingRequest = async (
  meetingRequestId: number,
  newDate: string,
): Promise<Response<MeetingRequest>> => {
  const response = await api.put(
    `app-web/events/meeting-requests/${meetingRequestId}/replan`,
    { meeting_date: newDate },
  );
  return response.data;
};

export const setUserType = async (
  eventId: number,
  userId: number,
  type: 'exhibitor' | 'visitor' | 'expert' | 'organizer',
): Promise<Response<EventUser>> => {
  const response = await api.post(
    `app-web/events/${eventId}/users/${userId}/type`,
    { type },
  );
  return response.data;
};
